import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Avatar,
  Breadcrumb,
  Button,
  Card,
  Col,
  Descriptions,
  Form,
  Input,
  Modal,
  Space,
  Tag,
  Typography,
  message,
} from 'antd';
import {
  EditOutlined,
  HomeOutlined,
  IdcardOutlined,
  LockOutlined,
  SafetyOutlined,
  UndoOutlined,
  UploadOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { updatePassword, updateProfile } from 'firebase/auth';
import SEO from './SEO';
import {
  formItemLayout,
  notificationTimeout,
  notificationType,
  userInfoLimits,
} from '../common/constants';
import debugLog from '../common/debugLog';
import useAuthClaims from '../common/useAuthClaims';
import {
  firGetSiteNameSlug,
  firGetUserData,
  firUpdateOwnProfile,
  primaryAuth,
} from '../firebase';
import './style.scss';

const { Text } = Typography;

// rev 83 (2026-05-16) — Profile "Pro" layout:
//  1. Identity card: avatar + fullname + role tag + status tag, plus
//     Descriptions block for email / phone (editable) / site / member
//     since / last sign-in / uid.
//  2. Permissions card: claim chips + plain-English summary of what
//     this role can do. Helps onboard new admins and debug "Admin
//     claim required" callable errors.
//  3. Change Display Name (existing).
//  4. Change Password (existing).
export default function Profile() {
  const claims = useAuthClaims();

  // Identity-card state
  const [accountData, setAccountData] = useState(null);
  const [siteName, setSiteName] = useState('');
  const [identityLoading, setIdentityLoading] = useState(true);
  const [phoneModalOpen, setPhoneModalOpen] = useState(false);
  const [phoneSaving, setPhoneSaving] = useState(false);
  const [phoneForm] = Form.useForm();

  // Change-password state (kept from original)
  const [buttonEnabled, setButtonEnabled] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();
  const [messageApi, contextHolder] = message.useMessage();

  // destructure imports
  const { colSpanLabel, colSpanWrapper } = formItemLayout;
  const { timeoutError, timeoutSuccess, timeoutWarning } = notificationTimeout;
  const { typeError, typeSuccess, typeWarning } = notificationType;
  const {
    fullnameLengthMax,
    fullnameLengthMin,
    passwordLengthMax,
    passwordLengthMin,
    validateDebounce,
  } = userInfoLimits;

  const openMessage = useCallback(
    (content, duration, type) => {
      messageApi.open({ content, duration, type });
    },
    [messageApi],
  );

  // ---------- Identity fetch ----------

  const fetchIdentity = useCallback(async () => {
    const uid = primaryAuth.currentUser?.uid;
    if (!uid) {
      setIdentityLoading(false);
      return;
    }
    setIdentityLoading(true);
    try {
      const data = await firGetUserData(uid);
      setAccountData(data || {});
      if (data?.assignedSite) {
        try {
          const ns = await firGetSiteNameSlug(data.assignedSite);
          setSiteName(ns?.name || '');
        } catch (e) {
          setSiteName('');
        }
      }
    } catch (e) {
      console.error('-- Error loading identity:', e);
    } finally {
      setIdentityLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchIdentity();
  }, [fetchIdentity]);

  // ---------- Derived UI helpers ----------

  // Maps accessLevel + claims to a coloured role tag.
  const roleTag = () => {
    if (claims.root) {
      return <Tag color="purple" icon={<SafetyOutlined />}>Root (ATACT Staff)</Tag>;
    }
    const level = accountData?.accessLevel;
    if (claims.superAdmin || level === 11) {
      return <Tag color="blue" icon={<SafetyOutlined />}>SuperAdmin</Tag>;
    }
    if (claims.admin || level === 12) {
      return <Tag color="cyan" icon={<SafetyOutlined />}>Admin</Tag>;
    }
    if (level === 21) return <Tag color="green">Owner</Tag>;
    if (level === 22) return <Tag color="lime">Master Tenant</Tag>;
    if (level === 23) return <Tag color="default">Tenant</Tag>;
    if (level != null) return <Tag>Level {level}</Tag>;
    return <Tag color="default">Unknown</Tag>;
  };

  const statusTag = () => {
    const s = accountData?.status;
    // rev 86 status enum: 0 Pending, 1 Active, 2 Deactivated
    if (s === 0) return <Tag color="warning">Pending</Tag>;
    if (s === 2) return <Tag color="error">Deactivated</Tag>;
    return <Tag color="success">Active</Tag>;
  };

  // Plain-English bullets for what this user's claims permit. Onboarding
  // aid + debug aid (shows what's missing when "Admin claim required"
  // errors come up).
  const permissionsSummary = () => {
    if (claims.root) {
      return [
        'Manage all sites across the project (Manage Sites tab)',
        'Create and reassign SuperAdmins for any site',
        'Inherit all SuperAdmin + Admin powers (cross-site)',
      ];
    }
    if (claims.superAdmin) {
      return [
        'Manage your assigned site: blocks, units, vehicles, residents, door channels, account-application config',
        'Approve, reject, or request more info on new account applications',
        'Invite and remove Admins (Site Config → Site Staff tab)',
      ];
    }
    if (claims.admin) {
      return [
        'Approve, reject, or request more info on new account applications',
        'Manage the vehicle whitelist',
        'Update door-channel labels and the visitor-entry flag',
        'View residents',
      ];
    }
    return ['Resident-level access only — no admin-gated features.'];
  };

  const claimChips = () => {
    const chips = [];
    if (claims.root)        chips.push(<Tag key="root"        color="purple">root</Tag>);
    if (claims.superAdmin)  chips.push(<Tag key="superAdmin"  color="blue">superAdmin</Tag>);
    if (claims.admin)       chips.push(<Tag key="admin"       color="cyan">admin</Tag>);
    if (chips.length === 0) chips.push(<Tag key="none"        color="default">(no admin claims)</Tag>);
    return chips;
  };

  const fullname = accountData?.fullname
    || primaryAuth.currentUser?.displayName
    || '—';
  const email = accountData?.email || primaryAuth.currentUser?.email || '—';
  const phone = accountData?.contactNumber || '';
  const uid = primaryAuth.currentUser?.uid || '';
  const isDebug = accountData?.isDebugEnabled === true;

  // ---------- Edit-phone modal ----------

  const openPhoneEdit = () => {
    setPhoneModalOpen(true);
  };

  const populatePhoneForm = useCallback(() => {
    phoneForm.resetFields();
    phoneForm.setFieldsValue({ contactNumber: phone });
  }, [phone, phoneForm]);

  const handlePhoneOk = async () => {
    try {
      const values = await phoneForm.validateFields();
      const u = primaryAuth.currentUser?.uid;
      if (!u) return;
      setPhoneSaving(true);
      await firUpdateOwnProfile(u, { contactNumber: (values.contactNumber || '').trim() });
      openMessage('Phone number updated', timeoutSuccess, typeSuccess);
      setPhoneModalOpen(false);
      phoneForm.resetFields();
      fetchIdentity();
    } catch (e) {
      if (e.errorFields) return;
      console.error('-- Error updating phone:', e);
      openMessage(`Error: ${e.message}`, timeoutError, typeError);
    } finally {
      setPhoneSaving(false);
    }
  };

  // ---------- Original Change Display Name ----------

  // Note: this writes Auth.displayName which is read by some legacy
  // paths. The MA web sidebar (rev 74) reads accounts/{uid}.fullname
  // first, so we also write Firestore via firUpdateOwnProfile so the
  // two stay in sync.
  const changeDisplayName = async (displayName) => {
    const u = primaryAuth.currentUser;
    if (!u) return;
    try {
      await updateProfile(u, { displayName });
      await firUpdateOwnProfile(u.uid, { fullname: displayName });
      debugLog('-- Display name changed successfully');
      openMessage('Display name updated', timeoutSuccess, typeSuccess);
      fetchIdentity();
    } catch (error) {
      console.error('-- Display name could not be changed:', error);
      openMessage(
        `Display name could not be changed: ${error.message}`,
        timeoutError,
        typeError,
      );
    }
  };

  const changePassword = (values) => {
    setLoading(true);

    if (values.password1 !== values.password2) {
      setLoading(false);
      console.warn('-- Passwords do not match');
      openMessage('Passwords do not match', timeoutWarning, typeWarning);
      return;
    }

    updatePassword(primaryAuth.currentUser, values.password2)
      .then(() => {
        form.resetFields();
        setButtonEnabled(false);
        setLoading(false);
        debugLog('-- Password changed successfully');
        openMessage('Password updated', timeoutSuccess, typeSuccess);
      })
      .catch((error) => {
        form.resetFields();
        setLoading(false);
        console.error('-- Password could not be changed:', error);
        openMessage(
          `Password could not be changed: ${error.message}`,
          timeoutError,
          typeError,
        );
      });
  };

  const handleReset = () => {
    form.resetFields();
    setLoading(false);
    debugLog('-- Form reset');
  };

  const watchedValues = Form.useWatch([], form);
  useEffect(() => {
    form
      .validateFields({ validateOnly: true })
      .then(() => setButtonEnabled(true))
      .catch(() => setButtonEnabled(false));
  }, [form, watchedValues]);

  return (
    <>
      <SEO
        description="Adjust profile settings"
        name="qiwei"
        title="Profile"
        type="webapp"
      />
      <div>
        <Breadcrumb
          items={[
            {
              title: (
                <Link to="..">
                  <HomeOutlined className="dropdown-icon-margin" />
                  <span>Home</span>
                </Link>
              ),
            },
            { title: <span>Profile</span> },
          ]}
        />
      </div>
      {contextHolder}

      {/* ----- 1. Identity & Account ----- */}
      <Card
        className="setting card"
        hoverable
        loading={identityLoading}
        title={
          <Space size="middle" align="center">
            <Avatar
              src={accountData?.avatarUrl || undefined}
              icon={<UserOutlined />}
              size={48}
            />
            <div>
              <div style={{ fontSize: 16, fontWeight: 600 }}>{fullname}</div>
              <Space size={6} wrap>
                {roleTag()}
                {statusTag()}
                {isDebug && (
                  <Tag color="orange">isDebugEnabled</Tag>
                )}
              </Space>
            </div>
          </Space>
        }
      >
        <Descriptions
          bordered={false}
          column={1}
          colon
          size="middle"
          items={[
            { key: 'email', label: 'Email', children: email },
            {
              key: 'phone',
              label: 'Phone',
              children: (
                <Space>
                  <span>{phone || <Text type="secondary">Not set</Text>}</span>
                  <Button
                    icon={<EditOutlined />}
                    onClick={openPhoneEdit}
                    size="small"
                    type="link"
                  >
                    Edit
                  </Button>
                </Space>
              ),
            },
            {
              key: 'site',
              label: 'Site',
              children: accountData?.assignedSite
                ? (
                  <Space direction="vertical" size={0} style={{ width: '100%' }}>
                    <span>{siteName || <Text type="secondary">(loading…)</Text>}</span>
                    <Text
                      copyable={{ text: accountData.assignedSite }}
                      style={{
                        fontFamily: 'monospace',
                        fontSize: 12,
                        color: '#888',
                        wordBreak: 'break-all',
                        whiteSpace: 'normal',
                      }}
                    >
                      {accountData.assignedSite}
                    </Text>
                  </Space>
                )
                : <Text type="secondary">No site assigned</Text>,
            },
            {
              key: 'uid',
              label: 'User ID',
              children: (
                <Text
                  copyable={{ text: uid }}
                  style={{ fontFamily: 'monospace', fontSize: 12 }}
                >
                  {uid || '—'}
                </Text>
              ),
            },
            {
              key: 'createdAt',
              label: 'Member Since',
              children: accountData?.createdAt || <Text type="secondary">—</Text>,
            },
            {
              key: 'lastSignIn',
              label: 'Last Sign-in',
              children: accountData?.lastSignIn || <Text type="secondary">—</Text>,
            },
          ]}
        />
      </Card>

      {/* ----- 2. Permissions ----- */}
      <Card
        className="setting card"
        hoverable
        title={
          <Space>
            <IdcardOutlined />
            <span>Permissions</span>
          </Space>
        }
      >
        <div style={{ marginBottom: 12 }}>
          <Text type="secondary" style={{ marginRight: 8 }}>Custom claims:</Text>
          <Space size={4} wrap>
            {claimChips()}
          </Space>
        </div>
        <Text type="secondary">As this role you can:</Text>
        <ul style={{ margin: '8px 0 0 0', paddingLeft: 20 }}>
          {permissionsSummary().map((line) => (
            <li key={line} style={{ marginBottom: 4 }}>{line}</li>
          ))}
        </ul>
        {!claims.ready && (
          <Text type="secondary" style={{ display: 'block', marginTop: 8, fontSize: 12 }}>
            (loading claims…)
          </Text>
        )}
      </Card>

      {/* ----- 3. Change Password ----- */}
      <Card
        actions={[
          <Button
            disabled={!buttonEnabled}
            icon={<UploadOutlined />}
            loading={loading}
            onClick={form.submit}
            type="link"
          >
            Submit
          </Button>,
          <Button
            danger
            icon={<UndoOutlined />}
            onClick={handleReset}
            type="link"
          >
            Reset
          </Button>,
        ]}
        className="setting card"
        hoverable
        loading={loading}
        title="Change Password"
      >
        <Form
          className="setting form"
          colon={false}
          form={form}
          id="changePassword"
          labelCol={{ span: colSpanLabel }}
          labelWrap
          name="changePassword"
          onFinish={changePassword}
          scrollToFirstError
          variant="filled"
          wrapperCol={{ span: colSpanWrapper }}
        >
          <Form.Item
            hasFeedback
            id="password1"
            label="Enter New Password"
            name="password1"
            rules={[
              {
                max: passwordLengthMax,
                message: `Password must be between ${passwordLengthMin}-${passwordLengthMax} characters`,
                min: passwordLengthMin,
                required: true,
              },
            ]}
            validateDebounce={validateDebounce}
            validateFirst
          >
            <Col span={colSpanWrapper}>
              <Input.Password
                addonBefore={<LockOutlined />}
                autoComplete="new-password"
                placeholder="required"
                showCount
              />
            </Col>
          </Form.Item>
          <Form.Item
            dependencies={['password1']}
            hasFeedback
            id="password2"
            label="Confirm Password"
            name="password2"
            rules={[
              {
                max: passwordLengthMax,
                min: passwordLengthMin,
                required: true,
              },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('password1') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('Passwords do not match'));
                },
              }),
            ]}
            validateDebounce={validateDebounce}
          >
            <Col span={colSpanWrapper}>
              <Input.Password
                addonBefore={<LockOutlined />}
                autoComplete="new-password"
                placeholder="confirm"
                showCount
              />
            </Col>
          </Form.Item>
        </Form>
      </Card>

      {/* ----- 4. Change Display Name ----- */}
      <Card
        className="setting card"
        hoverable
        title="Change Display Name"
      >
        <Form
          className="setting form"
          colon={false}
          id="changeDisplayName"
          labelCol={{ span: colSpanLabel }}
          labelWrap
          name="changeDisplayName"
          scrollToFirstError
          variant="filled"
          wrapperCol={{ span: colSpanWrapper }}
        >
          <Form.Item
            hasFeedback
            label="New Display Name"
            rules={[
              {
                max: fullnameLengthMax,
                message: `Display Name must be between ${fullnameLengthMin}-${fullnameLengthMax} characters`,
                min: fullnameLengthMin,
                required: true,
              },
            ]}
            validateDebounce={validateDebounce}
          >
            <Col span={colSpanWrapper}>
              <Input
                // key forces remount when fullname loads from Firestore
                // (async). Without it, defaultValue captures the initial
                // "—" placeholder and the field appears empty.
                key={fullname}
                defaultValue={fullname !== '—' ? fullname : ''}
                id="displayName"
                name="displayName"
                onPressEnter={(e) => changeDisplayName(e.target.value)}
                showCount
              />
            </Col>
          </Form.Item>
        </Form>
      </Card>

      {/* ----- Edit Phone modal ----- */}
      <Modal
        title="Edit Phone Number"
        open={phoneModalOpen}
        onOk={handlePhoneOk}
        confirmLoading={phoneSaving}
        onCancel={() => {
          setPhoneModalOpen(false);
          phoneForm.resetFields();
        }}
        okText="Save"
        afterOpenChange={(open) => { if (open) populatePhoneForm(); }}
        destroyOnClose
      >
        <Form form={phoneForm} layout="vertical" preserve={false}>
          <Form.Item
            label="Phone Number"
            name="contactNumber"
            tooltip="Shown in the Residents tab + used as a fallback contact for site staff. Leave blank to clear."
          >
            <Input placeholder="e.g., +65 9123 4567" maxLength={30} />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}
